/** The blog bodies are markdown written by the AI or by hand — just enough of it rendered.
 *  Inline spans are parsed into React nodes; nothing is ever handed to dangerouslySetInnerHTML. */
const TOKEN = /(\*\*[^*]+\*\*|__[^_]+__|\*[^*\n]+\*|_[^_\n]+_|`[^`]+`|\[[^\]]+\]\([^)\s]+\))/g;

export const inline = (text = "") =>
  String(text).split(TOKEN).filter(Boolean).map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return <strong key={i} className="font-semibold text-slate-100">{part.slice(2, -2)}</strong>;
    }
    if (part.startsWith("__") && part.endsWith("__")) {
      return <strong key={i} className="font-semibold text-slate-100">{part.slice(2, -2)}</strong>;
    }
    if (part.length > 2 && part.startsWith("*") && part.endsWith("*")) {
      return <em key={i} className="italic">{part.slice(1, -1)}</em>;
    }
    if (part.length > 2 && part.startsWith("_") && part.endsWith("_")) {
      return <em key={i} className="italic">{part.slice(1, -1)}</em>;
    }
    if (part.startsWith("`") && part.endsWith("`")) {
      return <code key={i} className="rounded bg-ink-800 px-1.5 py-0.5 font-mono text-[0.85em] text-gold">{part.slice(1, -1)}</code>;
    }
    const link = part.match(/^\[([^\]]+)\]\(([^)\s]+)\)$/);
    if (link) {
      // Operator-authored copy renders on the shared platform origin — only safe schemes.
      const safe = /^(https?:|mailto:|tel:|\/|#)/i.test(link[2]) ? link[2] : "#";
      const external = /^https?:\/\//i.test(safe);
      return (
        <a key={i} href={safe} className="text-gold underline underline-offset-2"
          {...(external ? { target: "_blank", rel: "noreferrer noopener" } : {})}>
          {link[1]}
        </a>
      );
    }
    return <span key={i}>{part}</span>;
  });

export const Markdown = ({ text = "" }) => {
  const blocks = [];
  let items = [];
  let ordered = false;

  const flush = () => {
    if (!items.length) return;
    const List = ordered ? "ol" : "ul";
    blocks.push(
      <List key={`l${blocks.length}`} className="my-4 space-y-2 pl-5 text-slate-300">
        {items.map((item, i) => (
          <li key={i} className={ordered ? "list-decimal" : "list-disc"}>{inline(item)}</li>
        ))}
      </List>);
    items = [];
  };

  for (const raw of String(text).split("\n")) {
    const line = raw.trim();
    const numbered = line.match(/^(\d{1,2})[.)]\s+(.*)$/);
    if (line.startsWith("- ") || line.startsWith("* ")) {
      if (ordered) flush();
      ordered = false;
      items.push(line.slice(2));
      continue;
    }
    if (numbered) {
      if (!ordered) flush();
      ordered = true;
      items.push(numbered[2]);
      continue;
    }
    flush();
    if (!line) continue;
    if (line.startsWith("### ")) {
      blocks.push(<h3 key={blocks.length} className="mt-8 font-display text-lg text-slate-100">{inline(line.slice(4))}</h3>);
    } else if (line.startsWith("## ")) {
      blocks.push(<h2 key={blocks.length} className="mt-10 font-display text-xl text-slate-100 sm:text-2xl">{inline(line.slice(3))}</h2>);
    } else if (line.startsWith("# ")) {
      blocks.push(<h2 key={blocks.length} className="mt-10 font-display text-2xl text-slate-100">{inline(line.slice(2))}</h2>);
    } else if (line.startsWith("> ")) {
      blocks.push(
        <blockquote key={blocks.length} className="mt-5 border-l-2 border-gold/50 pl-4 text-base italic leading-relaxed text-slate-400">
          {inline(line.slice(2))}
        </blockquote>);
    } else {
      blocks.push(<p key={blocks.length} className="mt-4 text-base leading-relaxed text-slate-300">{inline(line)}</p>);
    }
  }
  flush();
  return <div>{blocks}</div>;
};
